import type { IntegerAmount } from '@actual-app/core/shared/util';

import { LISTO_PARA_ASIGNAR } from './cubrir';
import type { OrigenCubrir } from './cubrir';
import { parseCuentasPagosFijos } from './pagosFijos';
import type { CuentasPagosFijos } from './pagosFijos';

/**
 * Cuenta de una categoría: de qué cuenta del presupuesto sale su gasto.
 * Sirve para que «Cubrir» proponga sacar dinero solo de categorías de la
 * misma cuenta que el gasto (lo del sueldo, de lo del sueldo; lo de la
 * cuenta común, de lo de la cuenta común).
 */

/** Meses de historial (incluido el actual) para la cuenta de una categoría. */
export const MESES_CUENTA = 12;
/** Parte del gasto (por importe) que tiene que salir de una cuenta. */
export const UMBRAL_CUENTA = 0.7;
/** Movimientos mínimos de esa cuenta para darla por buena. */
export const MIN_MOVIMIENTOS_CUENTA = 2;

/** Gasto de una categoría en una cuenta en un mes (una fila de la consulta). */
export type GastoCuentaMes = {
  categoria: string;
  cuenta: string;
  /** 'yyyy-MM'. */
  mes: string;
  /** Gasto en positivo. */
  importe: IntegerAmount;
  movimientos: number;
};

type Acumulado = { importe: number; movimientos: number };

function porCuenta(filas: readonly GastoCuentaMes[]): Map<string, Acumulado> {
  const r = new Map<string, Acumulado>();
  for (const f of filas) {
    const a = r.get(f.cuenta) ?? { importe: 0, movimientos: 0 };
    a.importe += Math.abs(f.importe);
    a.movimientos += f.movimientos;
    r.set(f.cuenta, a);
  }
  return r;
}

/**
 * Cuenta dominante del historial de UNA categoría: la que aporta al menos
 * `UMBRAL_CUENTA` del gasto (importe) con `MIN_MOVIMIENTOS_CUENTA`
 * movimientos o más. `null` si ninguna domina (empate, reparto o sin datos).
 */
export function cuentaDominante(
  filas: readonly GastoCuentaMes[],
): string | null {
  const cuentas = porCuenta(filas);
  let total = 0;
  for (const a of cuentas.values()) {
    total += a.importe;
  }
  if (total <= 0) {
    return null;
  }
  for (const [cuenta, a] of cuentas) {
    if (
      a.importe / total >= UMBRAL_CUENTA &&
      a.movimientos >= MIN_MOVIMIENTOS_CUENTA
    ) {
      return cuenta;
    }
  }
  return null;
}

/** Agrupa las filas de la consulta por categoría. */
export function filasPorCategoria(
  filas: readonly GastoCuentaMes[],
): Map<string, GastoCuentaMes[]> {
  const r = new Map<string, GastoCuentaMes[]>();
  for (const f of filas) {
    const lista = r.get(f.categoria) ?? [];
    lista.push(f);
    r.set(f.categoria, lista);
  }
  return r;
}

// ---------------------------------------------------------------------------
// Cuenta elegida a mano (pref `categorias-cuentas`), unificada con la de
// los pagos fijos (`pagos-fijos-cuentas`): UN solo dato por categoría.
// ---------------------------------------------------------------------------

/** Valor de `categorias-cuentas` para «Todas las cuentas». */
export const TODAS_LAS_CUENTAS = 'todas';

/** { idCategoría: idCuenta | 'todas' }; sin entrada = «Automática». */
export type CuentasElegidas = Record<string, string>;

/**
 * Cuentas elegidas, unificadas: si `categorias-cuentas` aún no existe (nunca
 * se ha escrito), se lee `pagos-fijos-cuentas` como respaldo (lo guardado
 * antes no se pierde: la primera escritura lo copia a la pref nueva). Una
 * vez escrita, la nueva es la única fuente (así «Automática» puede quitar
 * una cuenta que venía de la vieja).
 */
export function unificarCuentasElegidas(
  rawElegidas: string | null | undefined,
  rawPagosFijos: string | null | undefined,
): CuentasElegidas {
  return rawElegidas
    ? parseCuentasPagosFijos(rawElegidas)
    : parseCuentasPagosFijos(rawPagosFijos);
}

/** Lo que ven los pagos fijos: solo cuentas concretas (sin «todas»). */
export function cuentasDePagosFijos(
  elegidas: CuentasElegidas,
): CuentasPagosFijos {
  const r: CuentasPagosFijos = {};
  for (const [categoria, valor] of Object.entries(elegidas)) {
    if (valor !== TODAS_LAS_CUENTAS) {
      r[categoria] = valor;
    }
  }
  return r;
}

/** Añade solo las que aún no tienen elección (ni «todas»). */
export function conCuentasNuevas(
  elegidas: CuentasElegidas,
  nuevas: CuentasPagosFijos,
): CuentasElegidas {
  const r = { ...elegidas };
  for (const [categoria, cuenta] of Object.entries(nuevas)) {
    if (!(categoria in r)) {
      r[categoria] = cuenta;
    }
  }
  return r;
}

/** Elección de la ficha para una categoría. */
export type EleccionCuenta =
  | { tipo: 'automatica' }
  | { tipo: 'todas' }
  | { tipo: 'cuenta'; cuenta: string };

export function eleccionDe(
  elegidas: CuentasElegidas,
  categoria: string,
  validas: ReadonlySet<string>,
): EleccionCuenta {
  const valor = elegidas[categoria];
  if (valor === TODAS_LAS_CUENTAS) {
    return { tipo: 'todas' };
  }
  return valor && validas.has(valor)
    ? { tipo: 'cuenta', cuenta: valor }
    : { tipo: 'automatica' };
}

/**
 * Cuenta de cada categoría, por prioridad: 1) la elegida a mano en la ficha
 * o la de su pago fijo (es el mismo dato, `elegidas`); «todas» = sin cuenta
 * clara (se ofrece siempre); 2) la cuenta común, si Vadym la marcó como
 * categoría de la cuenta común; 3) la dominante del historial. `null` =
 * «sin cuenta clara». Solo valen cuentas de `validas` (abiertas y del
 * presupuesto).
 */
export function cuentaDeCategoria({
  categoria,
  historial,
  elegidas,
  comunes,
  cuentaComunId,
  validas,
}: {
  categoria: string;
  historial: readonly GastoCuentaMes[];
  elegidas: CuentasElegidas;
  comunes: ReadonlySet<string>;
  cuentaComunId: string | null | undefined;
  validas: ReadonlySet<string>;
}): string | null {
  const eleccion = eleccionDe(elegidas, categoria, validas);
  if (eleccion.tipo === 'todas') {
    return null;
  }
  if (eleccion.tipo === 'cuenta') {
    return eleccion.cuenta;
  }
  return cuentaAutomatica({
    categoria,
    historial,
    comunes,
    cuentaComunId,
    validas,
  });
}

/**
 * Cuenta «Automática» (sin elección a mano): la común si está marcada; si
 * no, la dominante del historial; `null` = sin cuenta clara.
 */
export function cuentaAutomatica({
  categoria,
  historial,
  comunes,
  cuentaComunId,
  validas,
}: {
  categoria: string;
  historial: readonly GastoCuentaMes[];
  comunes: ReadonlySet<string>;
  cuentaComunId: string | null | undefined;
  validas: ReadonlySet<string>;
}): string | null {
  if (cuentaComunId && validas.has(cuentaComunId) && comunes.has(categoria)) {
    return cuentaComunId;
  }
  const dominante = cuentaDominante(historial);
  return dominante && validas.has(dominante) ? dominante : null;
}

/**
 * Cuenta del gasto a cubrir: la de los movimientos de la categoría en rojo
 * de `mes` (la que más gasto aporta este mes); si no hay movimientos este
 * mes, o empatan, la cuenta de la categoría.
 */
export function cuentaDelGasto({
  historial,
  mes,
  cuentaCategoria,
  validas,
}: {
  historial: readonly GastoCuentaMes[];
  mes: string;
  cuentaCategoria: string | null;
  validas: ReadonlySet<string>;
}): string | null {
  const delMes = porCuenta(
    historial.filter(f => f.mes === mes && validas.has(f.cuenta)),
  );
  let mejor: string | null = null;
  let importe = 0;
  let empate = false;
  for (const [cuenta, a] of delMes) {
    if (a.importe > importe) {
      mejor = cuenta;
      importe = a.importe;
      empate = false;
    } else if (a.importe === importe && importe > 0) {
      empate = true;
    }
  }
  return mejor && !empate ? mejor : cuentaCategoria;
}

/** Orígenes de otra cuenta, plegados tras «Mostrar también las de…». */
export type OrigenesOtraCuenta<T = OrigenCubrir> = {
  cuenta: string;
  origenes: T[];
};

/**
 * Reparte los orígenes según la cuenta del gasto: se ofrecen «Listo para
 * asignar» (dinero de todas las cuentas), los de la misma cuenta y los «sin
 * cuenta clara»; los de otra cuenta van aparte, por cuenta y en el orden de
 * `ordenCuentas`. Sin cuenta del gasto, todo se ofrece (como antes).
 */
export function repartirOrigenesPorCuenta<T extends { id: string }>({
  origenes,
  cuentaGasto,
  cuentaDe,
  ordenCuentas = [],
}: {
  origenes: readonly T[];
  cuentaGasto: string | null;
  cuentaDe: (categoria: string) => string | null | undefined;
  ordenCuentas?: readonly string[];
}): { visibles: T[]; otras: OrigenesOtraCuenta<T>[] } {
  if (!cuentaGasto) {
    return { visibles: [...origenes], otras: [] };
  }
  const visibles: T[] = [];
  const otras = new Map<string, T[]>();
  for (const o of origenes) {
    const cuenta = o.id === LISTO_PARA_ASIGNAR ? null : cuentaDe(o.id);
    if (!cuenta || cuenta === cuentaGasto) {
      visibles.push(o);
    } else {
      otras.set(cuenta, [...(otras.get(cuenta) ?? []), o]);
    }
  }
  const posicion = (c: string) => {
    const i = ordenCuentas.indexOf(c);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return {
    visibles,
    otras: [...otras.entries()]
      .sort(([a], [b]) => posicion(a) - posicion(b))
      .map(([cuenta, lista]) => ({ cuenta, origenes: lista })),
  };
}

/**
 * Traspaso que hay que hacer en el banco tras mover dinero entre categorías
 * de cuentas distintas (solo pasa con «Mostrar todas las categorías»).
 * `null` si alguna no tiene cuenta clara («Listo para asignar», «todas»…)
 * o si es la misma.
 */
export function traspasoPendiente(
  cuentaOrigen: string | null | undefined,
  cuentaDestino: string | null | undefined,
): { desde: string; hacia: string } | null {
  return cuentaOrigen && cuentaDestino && cuentaOrigen !== cuentaDestino
    ? { desde: cuentaOrigen, hacia: cuentaDestino }
    : null;
}
